import React, { useState } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import './FormMonivaeBooking.css';
import { useFormsSwitchedOff, PreviewFormNotice } from '../components/PreviewFormNotice';

const FormMonivaeBooking = () => {
  const [submitting, setSubmitting] = useState(false);
  const formsOff = useFormsSwitchedOff();
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [bookingData, setBookingData] = useState({
    studentFirstName: '',
    studentLastName: '',
    parentGuardianFirstName: '',
    parentGuardianLastName: '',
    whoFillingForm: '',
    whoFillingFormOther: '',
    email: '',
    emailBelongsTo: [],
    phoneNumber: '',
    phoneBelongsTo: [],
    serviceRequired: '',
    referralSource: [],
    preferredTime: '',
    message: '',
    contactPermission: []
  });

  const handleBookingInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setBookingData(prev => ({
        ...prev,
        [name]: checked 
          ? [...prev[name], value]
          : prev[name].filter(item => item !== value)
      }));
    } else {
      setBookingData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (formsOff) return;
    setSubmitting(true);
    setSubmitError('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/monivae-booking/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          student_first_name: bookingData.studentFirstName,
          student_last_name: bookingData.studentLastName,
          parent_guardian_first_name: bookingData.parentGuardianFirstName,
          parent_guardian_last_name: bookingData.parentGuardianLastName,
          who_filling_form: bookingData.whoFillingForm,
          who_filling_form_other: bookingData.whoFillingFormOther,
          email: bookingData.email,
          email_belongs_to: bookingData.emailBelongsTo.join(', '),
          phone: bookingData.phoneNumber,
          phone_belongs_to: bookingData.phoneBelongsTo.join(', '),
          service_required: bookingData.serviceRequired,
          referral_source: bookingData.referralSource.join(', '),
          preferred_time: bookingData.preferredTime,
          additional_info: bookingData.message,
          contact_method: bookingData.contactPermission.join(', ')
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitSuccess(true);
        // Reset form after 5 seconds
        setTimeout(() => {
          setSubmitSuccess(false);
          setBookingData({
            studentFirstName: '',
            studentLastName: '',
            parentGuardianFirstName: '',
            parentGuardianLastName: '',
            whoFillingForm: '',
            whoFillingFormOther: '',
            email: '',
            emailBelongsTo: [],
            phoneNumber: '',
            phoneBelongsTo: [],
            serviceRequired: '',
            referralSource: [],
            preferredTime: '',
            message: '',
            contactPermission: []
          });
        }, 5000);
      } else {
        setSubmitError(data.detail || 'Submission failed. Please try again.');
      }
    } catch (error) {
      setSubmitError('Network error. Please check your connection and try again.');
      console.error('Booking form error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="form-monivae-booking-standalone">
      <div className="form-container">
        <div className="form-header">
          <div className="form-branding">
            <img src="/logo.png" alt="Clinical Minds Psychology" className="form-logo" />
          </div>
          <h1 className="form-title">Book Your Appointment - Monivae</h1>
          <p className="form-subtitle">Complete the form below and we'll be in touch within 24 hours</p>
        </div>

        <div className="form-content">
          {submitSuccess ? (
            <div className="success-message">
              <CheckCircle size={48} className="success-icon" />
              <h3>Booking Request Sent!</h3>
              <p>Thank you for your booking request. We'll contact you within 24 hours to confirm your appointment.</p>
            </div>
          ) : (
            <form onSubmit={handleBookingSubmit} className="booking-form">
              {formsOff && <PreviewFormNotice />}
              <div className="privacy-notice">
                <p>We take your privacy seriously. All data and files you submit are securely stored in Australia in compliance with The Privacy Act (1988) and the Australian Privacy Principles (APPs). You can find full details in our <a href="https://www.clinicalminds.net/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</p>
              </div>
              
              {/* Student Name */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="studentFirstName">Student First Name *</label>
                  <input
                    type="text"
                    id="studentFirstName"
                    name="studentFirstName"
                    value={bookingData.studentFirstName}
                    onChange={handleBookingInputChange}
                    required
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="studentLastName">Student Last Name *</label>
                  <input
                    type="text"
                    id="studentLastName"
                    name="studentLastName"
                    value={bookingData.studentLastName}
                    onChange={handleBookingInputChange}
                    required
                    disabled={submitting}
                  />
                </div>
              </div>

              {/* Parent/Guardian Name */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="parentGuardianFirstName">Parent/Guardian First Name (if applicable)</label>
                  <input
                    type="text"
                    id="parentGuardianFirstName"
                    name="parentGuardianFirstName"
                    value={bookingData.parentGuardianFirstName}
                    onChange={handleBookingInputChange}
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="parentGuardianLastName">Parent/Guardian Last Name (if applicable)</label>
                  <input
                    type="text"
                    id="parentGuardianLastName"
                    name="parentGuardianLastName"
                    value={bookingData.parentGuardianLastName}
                    onChange={handleBookingInputChange}
                    disabled={submitting}
                  />
                </div>
              </div>

              {/* Who is filling out form */}
              <div className="form-group">
                <label htmlFor="whoFillingForm">Who is filling out this form? *</label>
                <select
                  id="whoFillingForm"
                  name="whoFillingForm"
                  value={bookingData.whoFillingForm}
                  onChange={handleBookingInputChange}
                  required
                  disabled={submitting}
                >
                  <option value="">Select...</option>
                  <option value="I am a student">I am a student</option>
                  <option value="I am the parent/guardian">I am the parent/guardian</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Show text field if "Other" is selected */}
              {bookingData.whoFillingForm === 'Other' && (
                <div className="form-group">
                  <label htmlFor="whoFillingFormOther">Please specify *</label>
                  <input
                    type="text"
                    id="whoFillingFormOther"
                    name="whoFillingFormOther"
                    value={bookingData.whoFillingFormOther}
                    onChange={handleBookingInputChange}
                    required
                    disabled={submitting}
                    placeholder="Please specify who is filling out this form"
                  />
                </div>
              )}

              {/* Email */}
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={bookingData.email}
                  onChange={handleBookingInputChange}
                  required
                  disabled={submitting}
                />
                <label style={{marginTop: '0.75rem', marginBottom: '0.5rem'}}>Email of:</label>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="emailBelongsTo"
                      value="Student"
                      checked={bookingData.emailBelongsTo.includes('Student')}
                      onChange={handleBookingInputChange}
                      disabled={submitting}
                    />
                    <span>Student</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="emailBelongsTo"
                      value="Parent/Guardian"
                      checked={bookingData.emailBelongsTo.includes('Parent/Guardian')}
                      onChange={handleBookingInputChange}
                      disabled={submitting}
                    />
                    <span>Parent/Guardian</span>
                  </label>
                </div>
              </div>

              {/* Phone Number */}
              <div className="form-group">
                <label htmlFor="phoneNumber">Phone Number *</label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={bookingData.phoneNumber}
                  onChange={handleBookingInputChange}
                  required
                  disabled={submitting}
                />
                <label style={{marginTop: '0.75rem', marginBottom: '0.5rem'}}>Phone number of:</label>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="phoneBelongsTo"
                      value="Student"
                      checked={bookingData.phoneBelongsTo.includes('Student')}
                      onChange={handleBookingInputChange}
                      disabled={submitting}
                    />
                    <span>Student</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="phoneBelongsTo"
                      value="Parent/Guardian"
                      checked={bookingData.phoneBelongsTo.includes('Parent/Guardian')}
                      onChange={handleBookingInputChange}
                      disabled={submitting}
                    />
                    <span>Parent/Guardian</span>
                  </label>
                </div>
              </div>

              {/* Service Required */}
              <div className="form-group">
                <label htmlFor="serviceRequired">What service do you require? *</label>
                <select
                  id="serviceRequired"
                  name="serviceRequired"
                  value={bookingData.serviceRequired}
                  onChange={handleBookingInputChange}
                  required
                  disabled={submitting}
                >
                  <option value="">Select a service...</option>
                  <option value="Therapy">Therapy</option>
                  <option value="Assessment">Assessment</option>
                  <option value="Not sure">Not sure</option>
                </select>
              </div>

              {/* Referral Source */}
              <div className="form-group">
                <label>Which of the following apply to you or your child (if you are the parent/guardian)? *</label>
                <div className="checkbox-group">
                  {['I have talked to the Monivae Wellbeing Team', 'A teacher suggested I refer', 'I have a GP referral/mental health care plan'].map(source => (
                    <label key={source} className="checkbox-label">
                      <input
                        type="checkbox"
                        name="referralSource"
                        value={source}
                        checked={bookingData.referralSource.includes(source)}
                        onChange={handleBookingInputChange}
                        disabled={submitting}
                      />
                      <span>{source}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Preferred Time */}
              <div className="form-group">
                <label htmlFor="preferredTime">What time of the day would you prefer? *</label>
                <select
                  id="preferredTime"
                  name="preferredTime"
                  value={bookingData.preferredTime}
                  onChange={handleBookingInputChange}
                  required
                  disabled={submitting}
                >
                  <option value="">Select a time...</option>
                  <option value="Anytime">Anytime</option>
                  <option value="Before 9am">Before 9am</option>
                  <option value="From 9am - 4pm">From 9am - 4pm</option>
                  <option value="After 4pm">After 4pm</option>
                </select>
              </div>

              {/* Message */}
              <div className="form-group">
                <label htmlFor="message">Tell us how we can help you? *</label>
                <p className="form-help">So we can be best placed to help, we would appreciate knowing a little bit about you</p>
                <textarea
                  id="message"
                  name="message"
                  value={bookingData.message}
                  onChange={handleBookingInputChange}
                  rows="4"
                  required
                  disabled={submitting}
                ></textarea>
              </div>

              {/* Contact Permission */}
              <div className="form-group">
                <label>Is it OK for us to call/email/text you in relation to your request? *</label>
                <div className="checkbox-group">
                  {['Call', 'Email', 'SMS'].map(method => (
                    <label key={method} className="checkbox-label">
                      <input
                        type="checkbox"
                        name="contactPermission"
                        value={method}
                        checked={bookingData.contactPermission.includes(method)}
                        onChange={handleBookingInputChange}
                        disabled={submitting}
                      />
                      <span>{method}</span>
                    </label>
                  ))}
                </div>
              </div>

              {submitError && (
                <div className="error-message">
                  <p>{submitError}</p>
                </div>
              )}

              <div className="form-footer">
                <p className="consent-text">
                  By submitting this form, you confirm that you have read and agree to our <a href="https://www.clinicalminds.net/privacy-policy" target="_blank" rel="noopener noreferrer">privacy policy</a>.
                </p>
                <button 
                  type="submit" 
                  className="btn-submit"
                  disabled={submitting || formsOff}
                >
                  {submitting ? (
                    <>
                      <span className="spinner"></span>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Submit Booking Request
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormMonivaeBooking;